/** The Find in chat bar (chats.js chat-inline-search-wrap): words, hit counter, older/newer. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { Spinner } from '../components/Spinner';
import type { ChatSearch } from './useChatSearch';

interface BarProps {
  search: ChatSearch;
  onRun: (query: string) => void;
  onStep: (by: 1 | -1) => void;
  onClose: () => void;
}

export function FindBar({ search, onRun, onStep, onClose }: BarProps) {
  const { t } = useTranslation('chat');
  const [value, setValue] = useState(search.query);
  const hits = search.results?.length ?? 0;
  return (
    <div className="rc-find" role="search">
      <button type="button" className="rc-find-btn" aria-label={t('find.close')} title={t('find.close')} onClick={onClose}>
        <FeatherIcon name="x" size={14} />
      </button>
      <input
        type="search"
        autoFocus
        className="rc-find-field"
        aria-label={t('find.label')}
        placeholder={t('find.label')}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onRun(value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            onStep(1);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            onStep(-1);
          }
        }}
      />
      {search.busy && <Spinner size={12} label={t('find.searching')} />}
      {hits > 0 && (
        <span className="rc-find-nav">
          <span aria-live="polite">
            {search.index + 1}/{hits}
          </span>
          <button type="button" className="rc-find-btn" aria-label={t('find.older')} title={t('find.older')} disabled={search.index >= hits - 1} onClick={() => onStep(1)}>
            <FeatherIcon name="chevron-up" size={14} />
          </button>
          <button type="button" className="rc-find-btn" aria-label={t('find.newer')} title={t('find.newer')} disabled={search.index <= 0} onClick={() => onStep(-1)}>
            <FeatherIcon name="chevron-down" size={14} />
          </button>
        </span>
      )}
      <button type="button" className="rc-find-btn rc-find-run" aria-label={t('find.run')} title={t('find.run')} onClick={() => onRun(value)}>
        <FeatherIcon name="search" size={14} />
      </button>
    </div>
  );
}
